import {
	Checkbox,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	Select,
	Tabs,
	ValidationError,
} from "client/Components";
import { IpAddresses } from "client/Features/ConditionalAccess/IpAddresses";
import styles from "./WebDavOpenDocumentsSettingsTab.module.less";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	UserAndUserGroupCollection,
	UserAndUserGroupCollectionValue,
	UserAndUserGroupKind,
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { ActiveTab } from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { useCallback, useEffect, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { OfficeWebAppOpenTarget } from "client/Api/Documents/SharePoint/common";
import { WebDavOpenDocumentSettingsSaveModel } from "client/Api/WebDavSettings/webDavSettings";
import { IObjectInputAttachable } from "client/Forms/attachable";
import { OfficeDocumentEditor } from "client/Api/Documents/common";
import { SettingsPage } from "../SettingsPage";

const { Tab } = Tabs;

type WebDavOpenDocumentsSettingsTabProps = {
	settingsAttachable: IObjectInputAttachable<WebDavOpenDocumentSettingsSaveModel>;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	openOnlineConfigured: boolean;
};

const openTargets = [
	{
		value: OfficeWebAppOpenTarget.Popup,
		text: localize("_OpenInPopup"),
	},
	{
		value: OfficeWebAppOpenTarget.NewWorkspaceTab,
		text: localize("_OpenInWorkspaceFrame"),
	},
	{
		value: OfficeWebAppOpenTarget.NewOffice365Tab,
		text: localize("_NewTabUsingOfficeOnlineInterface"),
	},
];

export const WebDavOpenDocumentsSettingsTab = (
	props: WebDavOpenDocumentsSettingsTabProps
) => {
	const { settingsAttachable, usersAndUserGroupsData, openOnlineConfigured } =
		props;
	const {
		openLocalEnabled,
		openOnlineEnabled,
		openLocalPermissions,
		defaultOfficeDocumentEditor
	} = settingsAttachable.value;
	const { userIds, userGroupIds } = openLocalPermissions;

	const defaultDocumentEditorOptions: RadioButtonGroupItem<OfficeDocumentEditor>[] = useMemo(
		() => {
			return [
				{
					text: localize("_OfficeOnlineApps"),
					value: OfficeDocumentEditor.Online,
				},
				{
					text: localize("_OfficeLocalApps"),
					value: OfficeDocumentEditor.Local,
				},
			];
		},
		[]);

	const handleUserAndUserGroupChange = useCallback(
		(value: UserAndUserGroupCollectionValue) => {
			const { userGroupIds, userIds } = value;

			settingsAttachable
				.for("openLocalPermissions")
				.for("userIds")
				.reducer.setValue([...userIds]);

			settingsAttachable
				.for("openLocalPermissions")
				.for("userGroupIds")
				.reducer.setValue([...userGroupIds]);
		},
		[settingsAttachable]
	);

	const changeDefaultEditorHandler = useCallback(
		(newValue: OfficeDocumentEditor) => {
			settingsAttachable
				.for("defaultOfficeDocumentEditor")
				.reducer
				.setValue(newValue);
		},
		[settingsAttachable]
	);

	useEffect(
		() => {
			const editorsToChooseFrom: OfficeDocumentEditor[] = [];
			if (openOnlineEnabled) {
				editorsToChooseFrom.push(OfficeDocumentEditor.Online);
			}

			if (openLocalEnabled) {
				editorsToChooseFrom.push(OfficeDocumentEditor.Local);
			}

			if (
				editorsToChooseFrom.length
					&& editorsToChooseFrom.indexOf(defaultOfficeDocumentEditor) === -1
			) {
				changeDefaultEditorHandler(editorsToChooseFrom[0]);
			}
		},
		[
			changeDefaultEditorHandler,
			defaultOfficeDocumentEditor,
			openLocalEnabled,
			openOnlineEnabled
		]
	);

	return (
		<div className={styles.tabContent}>
			<FormItem
				label={localize("_UserIsAbleToOpenIn")}
			>
				{openOnlineConfigured && (
					<>
						<Checkbox
							{...settingsAttachable
								.for("openOnlineEnabled")
								.attachToCheckbox()}
							text={localize("_OpenInOfficeOnline")}
						/>
						<ValidationError
							{...settingsAttachable
								.for("openOnlineEnabled")
								.attachToValidationError()}
						/>
					</>
				)}
				<Checkbox
					{...settingsAttachable
						.for("openLocalEnabled")
						.attachToCheckbox()}
					text={localize("_OpenInLocalApp")}
				/>
				<ValidationError
					{...settingsAttachable
						.for("openLocalEnabled")
						.attachToValidationError()}
				/>
			</FormItem>
			{openLocalEnabled && openOnlineEnabled && (
				<FormItem
					label={localize("_DefaultPreferenceForOpeningFiles")}
					isForGroup
				>
					<RadioButtonGroup
						{...settingsAttachable
							.for("defaultOfficeDocumentEditor")
							.attachToRadioButtonGroup()}
						options={defaultDocumentEditorOptions}
					/>
					<ValidationError
						{...settingsAttachable
							.for("openOnlineEnabled")
							.attachToValidationError()}
					/>
				</FormItem>
			)}
			{openOnlineEnabled && (
				<>
					<h3 className={styles.title}>
						{localize("_OfficeOnlineApps")}
					</h3>
					<div>
						<FormItem
							label={localize("_OfficeOnlineAppsOpenTarget")}
						>
							<Select
								{...settingsAttachable
									.for("onlineEditorsOpenTarget")
									.attachToSelect()}
								options={openTargets}
								menuTitle={localize("_OfficeOnlineAppsOpenTarget")}
							/>
						</FormItem>
					</div>
				</>
			)}
			{openLocalEnabled && (
				<>
					<h3 className={styles.title}>
						{localize("_OfficeLocalApps")}
					</h3>
					<Tabs>
						<Tab
							id="who-has-access"
							title={localize("_WhoHasAccess")}
						>
							<div>
								<FormItem>
									<Checkbox
										{...settingsAttachable
											.for("openLocalPermissions")
											.for("everyoneHasAccess")
											.attachToCheckbox()}
										text={localize("_EveryoneHasAccess")}
									/>
								</FormItem>
							</div>
							{!openLocalPermissions.everyoneHasAccess && (
								<SettingsPage.Section height="stretch">
									<UserAndUserGroupCollection
										allUsersAndUserGroupsData={
											usersAndUserGroupsData
										}
										value={{
											userGroupIds,
											userIds,
										}}
										mode={{
											kind: UserAndUserGroupKind.Edit,
											dialogTitleWhenAddingUsersAndUserGroups:
												localize("_AddGroupsOrUsers"),
											onChange: handleUserAndUserGroupChange,
										}}
										activeTab={ActiveTab.UserGroup}
									/>
								</SettingsPage.Section>
							)}
						</Tab>
						<Tab
							{...settingsAttachable
								.under("openLocalConditionalAccessSettings")
								.attachToTab()}
							id="conditional-access"
							title={localize("_ConditionalAccess")}
						>
							<FormItem
								label={localize("_SupportedOn")}
								isForGroup
							>
								<IpAddresses
									attachable={settingsAttachable.for(
										"openLocalConditionalAccessSettings"
									)}
								/>
							</FormItem>
						</Tab>
					</Tabs>
				</>
			)}
		</div>
	);
};
